/*
 * Avisos al móvil (Web Push), sin instalar ninguna app.
 *
 * - Android / ordenador (Chrome, Edge, Firefox, Samsung Internet): directamente desde la web.
 * - iPhone / iPad (iOS 16.4+): Apple solo los permite si la web se ha añadido a la
 *   pantalla de inicio (Compartir → Añadir a pantalla de inicio). El componente
 *   AvisosMovil explica los pasos.
 *
 * El service worker (public/sw.js) recibe el aviso y lo muestra; la API guarda
 * la suscripción del dispositivo (/avisos/suscribir).
 */

import Vue from "vue";
import axios from "@/directives/axios.js";
import { track } from "@/directives/analitica.js";

const API = (process.env.VUE_APP_API_URL || "").replace(/\/+$/, "");

export const estadoAvisos = Vue.observable({
  // "cargando" | "no-soportado" | "ios-instalar" | "bloqueado" | "activos" | "inactivos" | "no-disponible"
  estado: "cargando",
  trabajando: false,
});

const esIOS = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

const instalada = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

const soportado = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

// La clave VAPID llega en base64url; el navegador la necesita en bytes
function claveABytes(base64) {
  const relleno = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + relleno).replace(/-/g, "+").replace(/_/g, "/");
  const bin = window.atob(b64);
  return Uint8Array.from([...bin].map((c) => c.charCodeAt(0)));
}

let registro = null;

/** Registra el service worker al arrancar la web (main.js). */
export async function iniciarAvisos() {
  if (!soportado()) {
    estadoAvisos.estado = esIOS() && !instalada() ? "ios-instalar" : "no-soportado";
    return;
  }
  try {
    registro = await navigator.serviceWorker.register(`/sw.js?api=${encodeURIComponent(API)}`, { scope: "/" });
    await actualizarEstado();
  } catch (e) {
    estadoAvisos.estado = "no-soportado";
  }
}

async function actualizarEstado() {
  if (Notification.permission === "denied") {
    estadoAvisos.estado = "bloqueado";
    return;
  }
  const sub = registro ? await registro.pushManager.getSubscription() : null;
  estadoAvisos.estado = sub ? "activos" : "inactivos";
}

export async function activarAvisos(origen = "web") {
  if (!registro || estadoAvisos.trabajando) return false;
  estadoAvisos.trabajando = true;
  try {
    const { data } = await axios.get("/avisos/clave");
    if (!data || !data.activo || !data.clave) {
      estadoAvisos.estado = "no-disponible";
      return false;
    }

    // El navegador pregunta aquí al visitante (solo tras pulsar el botón)
    const permiso = await Notification.requestPermission();
    if (permiso !== "granted") {
      estadoAvisos.estado = permiso === "denied" ? "bloqueado" : "inactivos";
      track("avisos_movil", { accion: "permiso_denegado", origen });
      return false;
    }

    let sub = await registro.pushManager.getSubscription();
    if (!sub) {
      sub = await registro.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: claveABytes(data.clave),
      });
    }
    await axios.post("/avisos/suscribir", sub.toJSON());

    estadoAvisos.estado = "activos";
    track("avisos_movil", { accion: "activar", origen });
    return true;
  } catch (e) {
    await actualizarEstado().catch(() => {});
    throw e;
  } finally {
    estadoAvisos.trabajando = false;
  }
}

export async function desactivarAvisos(origen = "web") {
  if (!registro || estadoAvisos.trabajando) return;
  estadoAvisos.trabajando = true;
  try {
    const sub = await registro.pushManager.getSubscription();
    if (sub) {
      await axios.post("/avisos/baja", { endpoint: sub.endpoint }).catch(() => {});
      await sub.unsubscribe();
    }
    estadoAvisos.estado = "inactivos";
    track("avisos_movil", { accion: "desactivar", origen });
  } finally {
    estadoAvisos.trabajando = false;
  }
}
